import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { useContext, useEffect, useMemo, useState } from 'react';
import { APIContext } from '../../Contexts/APIContext';
import MaintenanceDialogTitle from './MaintenanceDialogTitle';
import { MigrationRun, ReleaseVersion } from './ReleaseManagementTypes';

interface Props {
    open: boolean;
    onClose: () => void;
}

type MigrationPreview = {
    sourceVersion: ReleaseVersion;
    targetVersion: ReleaseVersion;
    candidateMissingSites: number;
    scheduledUtc: string;
    scheduledLocal: string;
    hasActiveRun: boolean;
};

const displayDate = (value?: string | null) => value ? new Date(value).toLocaleString('en-GB', {
    timeZone: 'Europe/London',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
}) : '—';

const statusColour = (status: MigrationRun['status']): 'default' | 'info' | 'success' | 'warning' | 'error' => {
    if (status === 'Scheduled') return 'info';
    if (status === 'Running') return 'warning';
    if (status === 'Completed') return 'success';
    if (status === 'CompletedWithFailures') return 'warning';
    if (status === 'Failed') return 'error';
    return 'default';
};

export default function MoveSitesToNextVersionDialog({ open, onClose }: Props) {
    const { get, post, showMessage } = useContext(APIContext);
    const [versions, setVersions] = useState<ReleaseVersion[]>([]);
    const [sourceId, setSourceId] = useState(0);
    const [preview, setPreview] = useState<MigrationPreview | null>(null);
    const [runs, setRuns] = useState<MigrationRun[]>([]);
    const [acknowledged, setAcknowledged] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const orderedVersions = useMemo(
        () => [...versions].sort((left, right) => new Date(left.start).getTime() - new Date(right.start).getTime() || left.id - right.id),
        [versions]
    );
    const sourceIndex = orderedVersions.findIndex(version => version.id === sourceId);
    const target = sourceIndex >= 0 ? orderedVersions[sourceIndex + 1] : undefined;

    const reloadRuns = () => get('Admin', 'GetVersionMigrationRuns', setRuns);

    useEffect(() => {
        if (!open) return;
        setAcknowledged(false);
        setConfirming(false);
        get('Admin', 'GetReleaseVersions', (items: ReleaseVersion[]) => {
            setVersions(items);
            const ordered = [...items].sort((left, right) => new Date(left.start).getTime() - new Date(right.start).getTime() || left.id - right.id);
            setSourceId(ordered.length > 1 ? ordered[ordered.length - 2].id : 0);
        });
        reloadRuns();
    }, [open]);

    useEffect(() => {
        setAcknowledged(false);
        setPreview(null);
        if (!open || !sourceId || !target) return;
        get('Admin', `GetVersionMigrationPreview?sourceVersionId=${sourceId}&targetVersionId=${target.id}`, setPreview);
    }, [open, sourceId, target?.id]);

    const schedule = async () => {
        if (!target) return;
        setSubmitting(true);
        await post('Admin', 'ScheduleVersionMigration', { sourceVersionId: sourceId, targetVersionId: target.id }, () => {
            showMessage('success', 'The one-time release migration was scheduled.');
            setConfirming(false);
            setAcknowledged(false);
            reloadRuns();
            get('Admin', `GetVersionMigrationPreview?sourceVersionId=${sourceId}&targetVersionId=${target.id}`, setPreview);
        });
        setSubmitting(false);
    };

    const close = () => {
        if (submitting) return;
        setConfirming(false);
        setAcknowledged(false);
        onClose();
    };

    const canContinue = !!preview && preview.candidateMissingSites > 0 && !preview.hasActiveRun && acknowledged && !submitting;

    return <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
        <MaintenanceDialogTitle
            icon={<ContentCopyOutlinedIcon />}
            title="Copy sites to the next version"
            description="Review and schedule a controlled overnight migration"
        />
        <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
                <Alert severity="warning">
                    This starts at 22:00 tonight (UK time). Large migrations are not guaranteed to have completed by the morning.
                </Alert>
                <Alert severity="info">
                    Existing target sites are untouched and all source-version data is retained.
                </Alert>

                <TextField
                    select
                    fullWidth
                    label="Source version"
                    value={sourceId}
                    onChange={event => setSourceId(Number(event.target.value))}
                >
                    {orderedVersions.slice(0, -1).map((version, index) => <MenuItem key={version.id} value={version.id}>
                        {version.versionName} → {orderedVersions[index + 1]?.versionName}
                    </MenuItem>)}
                </TextField>

                {preview && <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={0.75}>
                        <Typography><strong>Source:</strong> {preview.sourceVersion.versionName}</Typography>
                        <Typography><strong>Target:</strong> {preview.targetVersion.versionName}</Typography>
                        <Typography><strong>Missing target sites:</strong> {preview.candidateMissingSites}</Typography>
                        <Typography><strong>Scheduled start:</strong> {preview.scheduledLocal} (Europe/London)</Typography>
                        {preview.hasActiveRun && <Alert severity="warning">A migration is already scheduled or running for this target.</Alert>}
                        {preview.candidateMissingSites === 0 && <Alert severity="info">The target already contains every source site.</Alert>}
                    </Stack>
                </Paper>}

                <FormControlLabel
                    control={<Checkbox checked={acknowledged} onChange={event => setAcknowledged(event.target.checked)} />}
                    label="I understand this is a one-time overnight copy, may still be running in the morning, does not overwrite target sites, and does not delete source data."
                />

                <Divider />
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">Recent migration runs</Typography>
                    <Tooltip title="Reload run status">
                        <IconButton onClick={reloadRuns} size="small"><RefreshIcon /></IconButton>
                    </Tooltip>
                </Stack>

                {runs.length === 0 ? <Typography color="text.secondary">No migrations have been scheduled.</Typography> :
                    runs.map(run => <Paper key={run.id} variant="outlined" sx={{ p: 1.5 }}>
                        <Stack spacing={0.75}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1}>
                                <Typography variant="subtitle1"><strong>{run.operationMode}</strong> · <strong>{run.sourceVersionName}</strong> → <strong>{run.targetVersionName}</strong></Typography>
                                <Chip size="small" label={run.status.replace(/([a-z])([A-Z])/g, '$1 $2')} color={statusColour(run.status)} />
                            </Stack>
                            <Typography variant="body2">
                                {run.operationMode === 'Move' ? 'Moved' : 'Copied'} {run.copiedCount} · skipped existing {run.skippedExistingCount} · failed {run.failedCount} · total {run.totalCount}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Requested by {run.requestedBy || 'Unknown'} at {displayDate(run.requestedUtc)} · scheduled {displayDate(run.scheduledUtc)} · started {displayDate(run.startedUtc)} · completed {displayDate(run.completedUtc)}
                            </Typography>
                            {run.summary && <Typography variant="body2">{run.summary}</Typography>}
                            {run.failures.length > 0 && <Box>
                                <Typography variant="body2" color="error.main"><strong>Failed sites</strong></Typography>
                                {run.failures.map(failure => <Typography key={failure.siteReference} variant="caption" display="block" color="error.main">
                                    {failure.siteReference}: {failure.diagnostic || 'No diagnostic was recorded.'}
                                </Typography>)}
                            </Box>}
                        </Stack>
                    </Paper>)}
            </Stack>
        </DialogContent>
        <DialogActions>
            <Button onClick={close} disabled={submitting}>Close</Button>
            <Button variant="contained" color="warning" disabled={!canContinue} onClick={() => setConfirming(true)}>Review Schedule</Button>
        </DialogActions>

        <Dialog open={confirming} onClose={() => !submitting && setConfirming(false)} maxWidth="sm" fullWidth>
            <MaintenanceDialogTitle
                icon={<ReportProblemOutlinedIcon />}
                title="Final confirmation"
                description="Confirm the versions and scheduled start"
                tone="warning"
            />
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    <Alert severity="warning">
                        Schedule the one-time copy from <strong>{preview?.sourceVersion.versionName}</strong> to <strong>{preview?.targetVersion.versionName}</strong> for <strong>{preview?.scheduledLocal}</strong>?
                    </Alert>
                    <Typography variant="body2">
                        {preview?.candidateMissingSites} currently missing sites are candidates. The job is retry-safe, leaves existing target sites untouched, and retains the source data.
                    </Typography>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setConfirming(false)} disabled={submitting}>Back</Button>
                <Button onClick={schedule} disabled={submitting} variant="contained" color="warning">Schedule Migration</Button>
            </DialogActions>
        </Dialog>
    </Dialog>;
}
